import { Button, Dropdown, Radio, Segmented } from 'antd';
import { ListFilter } from 'lucide-react';
// eslint-disable-next-line no-use-before-define
import React from 'react';

import styles from './filters.module.css';

export type ProjectSearchSortDirection = {
  ascending: boolean;
  arrow: '↑' | '↓';
  label: string;
};

export type ProjectSearchSortOption<SortKey extends string = string> = {
  key: SortKey;
  label: string;
};

export type ProjectSearchSortByProps<SortKey extends string = string> = {
  buttonClassName?: string;
  buttonSize?: 'small' | 'middle' | 'large';
  directions: Partial<Record<SortKey, ProjectSearchSortDirection[]>>;
  getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
  manualSortHint?: string;
  onDirectionChange: (ascending: boolean) => void;
  onOpenChange: (open: boolean) => void;
  onSortChange: (sort: SortKey) => void;
  open: boolean;
  options: ProjectSearchSortOption<SortKey>[];
  sort: SortKey;
  sortAscending: boolean;
};

const SortBy = <SortKey extends string>({
  buttonClassName = '',
  buttonSize = 'small',
  directions,
  getPopupContainer,
  manualSortHint,
  onDirectionChange,
  onOpenChange,
  onSortChange,
  open,
  options,
  sort,
  sortAscending
}: ProjectSearchSortByProps<SortKey>) => {
  const activeDirections = directions[sort];
  const overlay = (
    <div className={styles.sortDropdownPanel}>
      <div className={styles.sortDirectionSection}>
        {activeDirections ? (
          <Segmented
            aria-label="Sort direction"
            block
            className={styles.sortDirectionControl}
            onChange={direction => onDirectionChange(direction === 'ascending')}
            options={activeDirections.map(direction => ({
              label: `${direction.arrow} ${direction.label}`,
              value: direction.ascending ? 'ascending' : 'descending'
            }))}
            size="small"
            value={sortAscending ? 'ascending' : 'descending'}
          />
        ) : (
          <p className={styles.manualSortHint}>{manualSortHint}</p>
        )}
      </div>
      <Radio.Group
        className={styles.sortOptions}
        onChange={event => onSortChange(event.target.value as SortKey)}
        value={sort}
      >
        {options.map(option => (
          <Radio
            className={styles.sortOption}
            key={option.key}
            value={option.key}
          >
            {option.label}
          </Radio>
        ))}
      </Radio.Group>
    </div>
  );

  return (
    <Dropdown
      getPopupContainer={
        getPopupContainer || (trigger => trigger.parentElement || document.body)
      }
      onOpenChange={onOpenChange}
      open={open}
      overlay={overlay}
      overlayClassName={styles.sortDropdownOverlay}
      placement="bottomRight"
      trigger={['click']}
    >
      <Button
        className={`${styles.filterPill} ${styles.sortButton} ${buttonClassName}`}
        icon={<ListFilter size={13} />}
        shape="round"
        size={buttonSize}
        type={open ? 'primary' : 'default'}
      >
        Sort
      </Button>
    </Dropdown>
  );
};

export default SortBy;
